import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography, useMediaQuery, useTheme, Box, Button,
    CircularProgress, Container,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';

// Reuse existing sub-components (unchanged)
import OperationCalendarView from './calendarSection/OperationCalendarView';
import OperationDetailPanel from './operationSection/OperationDetailPanel';
import TaskDetailView from './calendarSection/TaskDetailView';
import PopupAlert from '../../components/PopupAlert';
import MapViewDialog from '../../components/MapViewDialog';

import { getOperations } from '../../network/apis/missionlog.api';
import { getCalendarNotes, editCalendarNote, createOperationNote } from '../../network/apis/calendarNotes.api';
import { useAuth } from '../../context/authContext';
import { useProfile } from '../../context/userContext';
import { useOrganization } from '../../context/OrganizationContext';
import { hasPermission } from '../../data/menuData';

// CSS
import Styles from '../../styles/IndividualMissionLog.module.css';

// ─────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────
const OPERATIONS_PAGE_SIZE = 100;

const DEFAULT_STATUS = [
    'Authorized', 'Pending', 'Complete', 'De-authorized',
    'Deleted', 'Never Authorized', 'Invalid Detected', 'Rescinded Awaiting',
];

// ─────────────────────────────────────────────────────────────
//  COMPONENT
// ─────────────────────────────────────────────────────────────
const MissionCalendarPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // ── Auth / profile / org context ──────────────────────────
    const { token } = useAuth();
    const { profile } = useProfile();
    const { currentOrganization, isInOrganizationMode, userRole } = useOrganization();
    const userId = profile?.userId;

    // ── Operation state ───────────────────────────────────────
    const [allOperations, setAllOperations] = useState([]);
    const [displayedOperations, setDisplayedOperations] = useState([]);
    const [selectedOperation, setSelectedOperation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefreshing] = useState(false);

    // Calendar detail slide-in panel
    const [calDetailOpen, setCalDetailOpen] = useState(false);

    // ── Filter state ─────────────────────────────────────────
    const [sortOrder, setSortOrder] = useState('desc');
    const [filters, setFilters] = useState({});

    // ── Modals ────────────────────────────────────────────────
    const [alert, setAlert] = useState({ open: false, message: '', title: '', buttonText: '' });
    const [mapViewDialogOpen, setMapViewDialogOpen] = useState(false);
    const [mapViewOperation, setMapViewOperation] = useState(null);

    // ── Permissions ───────────────────────────────────────────
    const canEdit = !isInOrganizationMode || hasPermission(userRole, 'EDIT_CALENDAR');

    // ── File state ────────────────────────────────────────────
    const [files] = useState([]);

    // ── Load operations from API ─────────────────────────────
    const loadOperations = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const organizationId = currentOrganization?.organizationId || null;
            let operations = await getOperations(
                token, 1, OPERATIONS_PAGE_SIZE, {}, 'desc', DEFAULT_STATUS, organizationId, true
            );

            // Role-based filtering: pilots only see their own operations
            if (isInOrganizationMode && userRole?.toLowerCase() === 'pilot' && userId) {
                operations = operations.filter(op => op.user_id === userId);
            }

            // Fetch standalone calendar notes (not linked to any operation)
            let standaloneNotes = [];
            try {
                standaloneNotes = await getCalendarNotes(token);
            } catch (noteError) {
                console.error('Failed to fetch calendar notes:', noteError);
            }

            // Standardize standalone notes
            const formattedStandaloneNotes = standaloneNotes
                .filter(note => !note.operationId) // exclude operation-linked notes (already embedded)
                .map(note => ({
                    ...note,
                    is_note: true,
                    operation_id: `NOTE-${note.id}`,
                    date: note.date,
                    start_time: note.startTime,
                    duration: 30,
                    timezone: 'UTC',
                    flightZone: note.description ? note.description.substring(0, 30) : 'Reminder',
                    operator_name: 'Reminder',
                    authorizations: []
                }));

            const combinedData = [...operations, ...formattedStandaloneNotes];

            setAllOperations(combinedData);
            setDisplayedOperations(combinedData);
            setSelectedOperation(prev => {
                if (!prev) return prev;
                return combinedData.find(op => op.id === prev.id && op.is_note === prev.is_note) || prev;
            });
        } catch (error) {
            console.error('Error fetching operations:', error);
        } finally {
            setLoading(false);
        }
    }, [token, currentOrganization?.organizationId, isInOrganizationMode, userRole, userId]);

    useEffect(() => {
        if (!token) return;
        loadOperations();
    }, [token, loadOperations]);

    // ── Filter handlers ────────────────────────────────────────
    const handleSortChange = (order) => {
        setSortOrder(order);
        setDisplayedOperations([]);
        loadOperations(filters, order);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        setDisplayedOperations([]);
        setSortOrder('desc');
        setFilters({});
        setCalDetailOpen(false);
        loadOperations().finally(() => setRefreshing(false));
    };

    // ── Calendar event click ──────────────────────────────────
    const handleCalendarEventClick = (operation) => {
        setSelectedOperation(operation);
        setCalDetailOpen(true);
    };

    // Background clicks on the calendar are intentionally disabled.
    // Notes must be added via the Mission Log page.
    const handleCalendarBackgroundClick = () => {};

    // ── Add or edit note for a mission log operation ─────────
    const handleAddOperationNote = async (noteData) => {
        if (!token) return;
        try {
            if (noteData._isEdit) {
                await editCalendarNote(noteData.id, noteData, token);
                setAlert({ open: true, message: 'Note updated successfully.', title: 'Note Saved', buttonText: 'Great' });
            } else {
                await createOperationNote(noteData, token);
                setAlert({ open: true, message: 'Note added to operation.', title: 'Note Saved', buttonText: 'Great' });
            }
            await loadOperations();
        } catch (error) {
            console.error('Error saving operation note:', error);
            setAlert({ open: true, message: `Failed to save note: ${error.message}`, title: 'Error', buttonText: 'OK' });
        }
    };

    // ── Redirection logic ──────────────────────────────────────
    const redirectToMissionLog = (operationId) => {
        navigate('/missionlog', { state: { highlightOperationId: operationId } });
    };

    // Handle viewing operation on map
    const handleViewOnMap = (operation) => {
        setMapViewOperation(operation);
        setMapViewDialogOpen(true);
    };

    const handleDownload = (file) => {
        setAlert({ open: true, message: `Downloading ${file?.fileName || 'file'} (dummy).`, title: 'Info', buttonText: 'OK' });
    };

    const handleClose = () => setAlert(prev => ({ ...prev, open: false }));

    // ──────────────────────────────────────────────────────────
    //  JSX
    // ──────────────────────────────────────────────────────────
    return (
        <>
            <PopupAlert open={alert.open} handleClose={handleClose} message={alert.message} title={alert.title} buttonText={alert.buttonText} />


            <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>

                {/* ── Header ─────────────────────────────────────── */}
                <Box className={Styles.page_header}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarMonthIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' }, color: '#1e3a8a' }} />
                        <Typography
                            variant="h5"
                            component="h1"
                            className={Styles.page_title}
                            sx={{ fontSize: { xs: '1.375rem', sm: '1.75rem', md: '2rem' } }}
                        >
                            Operation Calendar <Box component="span" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, color: '#64748b', fontWeight: 500 }}>(Beta)</Box>
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            onClick={handleRefresh}
                            disabled={refresh}
                            startIcon={refresh ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                            sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' }, py: '6px', px: { xs: '10px', sm: '16px' } }}
                        >
                            {refresh ? 'Refreshing...' : (isMobile ? 'Refresh' : 'Refresh Calendar')}
                        </Button>
                    </Box>
                </Box>


                {/* ── Loading ────────────────────────────────────── */}
                {loading && (
                    <Box className={Styles.loading_overlay}>
                        <CircularProgress size={36} />
                        <Typography variant="body1" color="text.secondary">Loading Calendar…</Typography>
                    </Box>
                )}

                {/* ══════════════════════════════════════════════ */}
                {/* CALENDAR VIEW                                   */}
                {/* ══════════════════════════════════════════════ */}
                {!loading && (
                    <>
                        <OperationCalendarView
                            operations={displayedOperations}
                            onEventClick={handleCalendarEventClick}
                            onBackgroundClick={handleCalendarBackgroundClick}
                        />

                        {/* Backdrop */}
                        {calDetailOpen && (
                            <Box className={Styles.backdrop} onClick={() => setCalDetailOpen(false)} />
                        )}

                        {/* Slide-in detail panel */}
                        <Box className={`${Styles.calendar_detail_overlay} ${calDetailOpen ? Styles.calendar_detail_overlay_open : ''}`}>
                            <Box className={Styles.calendar_detail_header}>
                                <Typography className={Styles.calendar_detail_title}>
                                    {selectedOperation
                                        ? (selectedOperation.is_note ? 'Task Details' : `FLZ${selectedOperation.operation_id} — Operation Summary`)
                                        : 'Summary'}
                                </Typography>
                                <button className={Styles.calendar_detail_close} onClick={() => setCalDetailOpen(false)}>
                                    <CloseIcon sx={{ fontSize: 20 }} />
                                </button>
                            </Box>
                            <Box className={Styles.calendar_detail_body}>
                                {selectedOperation ? (
                                    selectedOperation.is_note ? (
                                        // Standalone calendar notes: view-only, edit via Mission Log
                                        <TaskDetailView
                                            operation={selectedOperation}
                                            onRedirect={() => redirectToMissionLog(selectedOperation?.operation_id)}
                                            onViewOnMap={handleViewOnMap}
                                            canEdit={false}
                                        />
                                    ) : (
                                        // Operation events: view-only panel, notes added via Mission Log or directly here if permitted
                                        <OperationDetailPanel
                                            operation={selectedOperation}
                                            files={files}
                                            onDownload={handleDownload}
                                            onRedirect={() => redirectToMissionLog(selectedOperation?.operation_id)}
                                            onViewOnMap={handleViewOnMap}
                                            isReadOnly={true}
                                            onAddNote={canEdit ? handleAddOperationNote : undefined}
                                        />
                                    )
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Select an event on the calendar to view details.</Typography>
                                )}
                            </Box>
                        </Box>
                    </>
                )}

                {/* Notes are managed via Mission Log — no create/edit modal on Calendar */}
            </Container>

            <MapViewDialog
                open={mapViewDialogOpen}
                onClose={() => {
                    setMapViewDialogOpen(false);
                    setMapViewOperation(null);
                }}
                operationId={mapViewOperation?.id}
                operationName={mapViewOperation ? `FLZ${mapViewOperation.operation_id}` : null}
            />
        </>
    );
};

export default MissionCalendarPage;
